import React, { useState, useEffect } from 'react';
import { AlertTriangle, Bell, Check, CheckCheck, ChevronDown, ExternalLink, Shield, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-900/40', border: 'border-red-700', text: 'text-red-300', badge: 'bg-red-600 text-white' },
  urgent: { bg: 'bg-amber-900/40', border: 'border-amber-700', text: 'text-amber-300', badge: 'bg-amber-600 text-white' },
  notable: { bg: 'bg-blue-900/30', border: 'border-blue-700', text: 'text-blue-300', badge: 'bg-blue-600 text-white' },
  routine: { bg: 'bg-gray-800/50', border: 'border-gray-700', text: 'text-gray-400', badge: 'bg-gray-600 text-gray-200' },
};

/* ── Dashboard Widget ─────────────────────────────────────────────── */

const AlertCenter = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const navigate = useNavigate();

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/alerts');
      setAlerts(resp.data || []);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(); const i = setInterval(fetchAlerts, 60000); return () => clearInterval(i); }, []);

  const handleAcknowledge = async (id) => {
    try {
      await api.post(`/alerts/${id}/acknowledge`);
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch (err) { console.error(err); }
  };

  const handleAckAllRoutine = async () => {
    try {
      await api.post('/alerts/acknowledge-all');
      fetchAlerts();
    } catch (err) { console.error(err); }
  };

  const filtered = tab === 'all' ? alerts
    : tab === 'urgent' ? alerts.filter(a => ['urgent', 'critical'].includes(a.severity))
    : alerts.filter(a => a.severity === tab);

  const criticals = alerts.filter(a => a.severity === 'critical');

  if (loading && alerts.length === 0) {
    return <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700"><div className="text-gray-500 text-sm animate-pulse">Loading alerts...</div></div>;
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-gray-800/30 rounded-xl p-6 text-center border border-gray-700/50">
        <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">No active alerts — all cases nominal</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 mb-4">
      {/* Critical banner */}
      {criticals.length > 0 && (
        <div className="bg-red-900/30 border border-red-700 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-bold text-red-300">{criticals.length} CRITICAL ALERT{criticals.length > 1 ? 'S' : ''}</span>
          </div>
          {criticals.map(a => (
            <div key={a.id} className="flex items-center justify-between py-1">
              <span className="text-sm text-red-200 cursor-pointer hover:underline" onClick={() => navigate(`/cases/${a.case_id}?tab=alerts`)}>
                {a.case_name} — {a.headline}
              </span>
              <button onClick={() => handleAcknowledge(a.id)} className="text-xs text-red-400 hover:text-red-200 px-2"><Check className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Alert list */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wide">Alerts</h3>
            <span className="text-xs bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded-full">{alerts.length}</span>
          </div>
          <button onClick={handleAckAllRoutine} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300">
            <CheckCheck className="w-3 h-3" /> Ack All Routine
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-3 border-b border-gray-700 pb-2">
          {['all', 'urgent', 'notable', 'routine'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-2 py-1 text-xs rounded ${tab === t ? 'bg-gray-700 text-gray-200' : 'text-gray-500 hover:text-gray-300'}`}
            >
              {t === 'urgent' ? 'Urgent/Critical' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filtered.map(alert => (
            <AlertCard key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} onNavigate={() => navigate(`/cases/${alert.case_id}?tab=alerts`)} />
          ))}
        </div>
      </div>
    </div>
  );
};

const AlertCard = ({ alert, onAcknowledge, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  const s = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.routine;

  return (
    <div className={`rounded-lg border p-3 ${s.bg} ${s.border} transition`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${s.badge}`}>{alert.severity}</span>
          <span className="text-sm text-gray-200 truncate cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); onNavigate(); }}>
            {alert.case_name}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-2">
          {alert.requires_immediate_action && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-800 text-red-200">ACTION</span>}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      <p className={`text-xs mt-1 ${s.text}`}>{alert.headline}</p>

      {expanded && (
        <div className="mt-2 space-y-2 border-t border-gray-700/50 pt-2">
          {alert.shift_type && <div className="text-[10px] text-gray-500">Type: {alert.shift_type}</div>}
          {alert.flags?.length > 0 && (
            <div>
              <span className="text-[10px] text-gray-500">Flags:</span>
              {alert.flags.map((f, i) => (
                <div key={i} className="ml-2 text-xs text-gray-400 mt-1">
                  <span className="text-gray-500 font-medium">{f.flag_type?.toUpperCase()}: </span>
                  {f.description}
                  {f.deadline_if_any && <span className="text-red-400 ml-1">(deadline: {f.deadline_if_any})</span>}
                  {f.recommended_action && <div className="text-teal-400 text-[10px] ml-2">→ {f.recommended_action}</div>}
                </div>
              ))}
            </div>
          )}
          {alert.suggested_next_steps?.length > 0 && (
            <div>
              <span className="text-[10px] text-gray-500">Next Steps:</span>
              <ol className="list-decimal list-inside text-xs text-gray-400 mt-1">
                {alert.suggested_next_steps.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={() => onAcknowledge(alert.id)} className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 text-gray-300">
              <Check className="w-3 h-3" /> Acknowledge
            </button>
            <span className="text-[10px] text-gray-600">{alert.generated_at ? new Date(alert.generated_at).toLocaleString() : ''}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Header Notification Bell ─────────────────────────────────────── */

export const AlertBell = () => {
  const [stats, setStats] = useState({ critical: 0, urgent: 0 });
  const [dropdown, setDropdown] = useState(false);
  const [topAlerts, setTopAlerts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [statsResp, urgentResp] = await Promise.all([
          api.get('/alerts/stats'),
          api.get('/alerts/urgent'),
        ]);
        setStats(statsResp.data || {});
        setTopAlerts((urgentResp.data || []).slice(0, 5));
      } catch (err) { /* silent */ }
    };
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  const count = (stats.critical || 0) + (stats.urgent || 0);
  if (count === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setDropdown(!dropdown)}
        className="relative p-2 text-gray-400 hover:text-gray-200 transition"
      >
        <Bell className="w-5 h-5" />
        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
          {count > 9 ? '9+' : count}
        </span>
      </button>

      {dropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setDropdown(false)} />
          <div className="absolute right-0 top-10 z-50 w-80 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
            <div className="p-3 border-b border-gray-800 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-400 uppercase">Urgent Alerts</span>
              <span className="text-xs text-red-400">{count} active</span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {topAlerts.map(a => {
                const s = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.routine;
                return (
                  <div
                    key={a.id}
                    className="px-3 py-2 border-b border-gray-800/50 hover:bg-gray-800/50 cursor-pointer"
                    onClick={() => { setDropdown(false); navigate(`/cases/${a.case_id}?tab=alerts`); }}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] px-1 py-0.5 rounded font-bold uppercase ${s.badge}`}>{a.severity}</span>
                      <span className="text-xs text-gray-300 truncate">{a.case_name}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5 truncate">{a.headline}</p>
                  </div>
                );
              })}
            </div>
            <div className="p-2 border-t border-gray-800 text-center">
              <button onClick={() => { setDropdown(false); navigate('/dashboard'); }} className="text-xs text-teal-400 hover:text-teal-300">
                View All Alerts
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ── Case Detail Alert Feed ───────────────────────────────────────── */

export const CaseAlertFeed = ({ caseId }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get(`/alerts/cases/${caseId}`);
        setAlerts(resp.data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [caseId]);

  const handleAcknowledge = async (id) => {
    try {
      await api.post(`/alerts/${id}/acknowledge`);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, acknowledged: true, acknowledged_at: new Date().toISOString() } : a));
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-4 text-gray-500 animate-pulse">Loading alerts...</div>;

  if (alerts.length === 0) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">No alerts for this case.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      {alerts.map(alert => {
        const s = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.routine;
        const acked = alert.acknowledged;

        return (
          <div key={alert.id} className={`rounded-lg border p-3 transition ${acked ? 'opacity-50 bg-gray-900/30 border-gray-800' : `${s.bg} ${s.border}`}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${s.badge}`}>{alert.severity}</span>
                {alert.shift_type && <span className="text-[10px] text-gray-500">{alert.shift_type}</span>}
                {acked && <span className="text-[10px] text-green-600">✓ Acknowledged</span>}
              </div>
              <span className="text-[10px] text-gray-600">{alert.generated_at ? new Date(alert.generated_at).toLocaleString() : ''}</span>
            </div>
            <p className={`text-sm mt-1 ${acked ? 'text-gray-500' : s.text}`}>{alert.headline}</p>
            {!acked && (
              <div className="mt-2">
                {alert.flags?.length > 0 && alert.flags.map((f, i) => (
                  <div key={i} className="text-xs text-gray-400 mt-1">
                    <span className="text-gray-500 font-medium">{f.flag_type?.toUpperCase()}: </span>{f.description}
                    {f.recommended_action && <span className="text-teal-400 ml-1">→ {f.recommended_action}</span>}
                  </div>
                ))}
                {alert.suggested_next_steps?.length > 0 && (
                  <ol className="list-decimal list-inside text-xs text-gray-400 mt-2">
                    {alert.suggested_next_steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>
                )}
                <button onClick={() => handleAcknowledge(alert.id)} className="mt-2 flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 text-gray-300">
                  <Check className="w-3 h-3" /> Acknowledge
                </button>
              </div>
            )}
            {acked && alert.acknowledged_by && (
              <div className="text-[10px] text-gray-600 mt-1">by {alert.acknowledged_by} · {alert.acknowledged_at ? new Date(alert.acknowledged_at).toLocaleDateString() : ''}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AlertCenter;
